import type { BoundingRegion } from "@/interface/pdfTypes";
import type { AnnotationConfig, AnnotationStrategy } from "./AnnotationStrategy";
import { UniformGridStrategy } from "./UniformGridStrategy";

/** Strategy assumed when a row predates the strategy column (all current rows set it). */
const DEFAULT_STRATEGY_TYPE = "uniform_grid";

/**
 * Decodes a field's persisted `box_ids` into the bounding regions the PDF viewer
 * renders, picking the decoder by the field's annotation strategy type.
 *
 * Box geometry lives entirely in `case_field_review` (`box_ids` +
 * `doc_annotation_strategy_type` + `doc_annotation_strategy_config`), so this
 * replaces the legacy `/get_raw_json_by_case_id` lookup as the source of
 * highlight regions for the case-review UI.
 */
export class BoundingBoxDecoder {
  private readonly strategies: Map<string, AnnotationStrategy>;

  constructor(strategies: AnnotationStrategy[] = [new UniformGridStrategy()]) {
    this.strategies = new Map(strategies.map((strategy) => [strategy.type, strategy]));
  }

  decode(
    boxIds: string[] | null | undefined,
    strategyType: string | null | undefined,
    config: AnnotationConfig | null | undefined,
  ): BoundingRegion[] {
    if (!boxIds || boxIds.length === 0) {
      return [];
    }
    const type = strategyType || DEFAULT_STRATEGY_TYPE;
    const strategy = this.strategies.get(type);
    if (!strategy) {
      console.warn(`BoundingBoxDecoder: no decoder for annotation strategy "${type}"; cannot draw highlight`);
      return [];
    }
    return strategy.decode(boxIds, config ?? null);
  }
}

/** Shared decoder instance for the app (uniform grid only, today). */
export const boundingBoxDecoder = new BoundingBoxDecoder();

/**
 * Convenience wrapper used by the case-review highlight handlers: decode a
 * field's persisted box annotation into PDF bounding regions.
 */
export function decodeBoundingBoxes(
  boxIds: string[] | null | undefined,
  strategyType: string | null | undefined,
  config: AnnotationConfig | null | undefined,
): BoundingRegion[] {
  return boundingBoxDecoder.decode(boxIds, strategyType, config);
}
